/**
 * CHAP-491 — scoring a capital stack against what a lender actually tests for.
 *
 * Merged from omega-club's `CapStackComparison.jsx`. Pure functions here rather
 * than inside the page, because this is the substance of that surface and it
 * should be testable without rendering anything.
 *
 * Typical CRE / student-housing-adjacent thresholds, carried over verbatim from
 * the omega-club component's header:
 *
 *   DSCR (senior)   >= 1.25x   (1.30x preferred for unique assets)
 *   LTV (senior)    <= 70%     on stabilized appraisal (lower for ground-up)
 *   Debt yield      >= 10%     (11%+ for unique assets like Greek housing)
 *   Sponsor equity  >= 25%     of total project cost
 *
 * Project cost is each scenario's OWN source total rather than one global
 * figure: the two stacks raise different amounts, and scoring both against a
 * single cost would misstate the equity share of whichever one differs.
 */
import { calculatePMT } from './financial.js';

export interface UnderwritingContext {
  /** Stabilised Year-1 NOI, in whole dollars. */
  noi: number;
  /** Cap rate as a percentage, e.g. 6.0 — not 0.06. */
  capRatePct: number;
  /** Senior amortisation term. */
  termYears: number;
}

export interface StackSource {
  type: string;
  amount: number;
  /** Percentage, e.g. 6.25 — not 0.0625. */
  interestRate?: number;
}

export interface UnderwritingTest {
  label: string;
  value: string;
  threshold: string;
  pass: boolean;
}

/** Source types that count as senior debt for the DSCR/LTV/debt-yield tests. */
export const SENIOR_DEBT_TYPES = new Set(['senior-debt']);

/**
 * Source types that count as sponsor equity. Gifts count: from a lender's point
 * of view, money that never has to be repaid is equity regardless of the tax
 * treatment that made it attractive to the giver. Subordinate debt and
 * mezzanine deliberately do NOT count — they are repayable.
 */
export const SPONSOR_EQUITY_TYPES = new Set(['gift', 'llc-equity', 'equity']);

export interface ScoredStack {
  dscr: number;
  ltv: number;
  debtYield: number;
  equityShare: number;
  seniorAmount: number;
  annualDebtService: number;
  stabilizedValue: number;
}

/** The raw ratios, before any formatting or pass/fail judgement. */
export function scoreStack(
  sources: StackSource[],
  total: number,
  u: UnderwritingContext,
): ScoredStack {
  const senior = sources.filter((x) => SENIOR_DEBT_TYPES.has(x.type));
  const seniorAmount = senior.reduce((n, x) => n + (x.amount || 0), 0);
  const seniorRate = (senior.find((x) => x.interestRate != null)?.interestRate ?? 0) / 100;
  const equity = sources
    .filter((x) => SPONSOR_EQUITY_TYPES.has(x.type))
    .reduce((n, x) => n + (x.amount || 0), 0);

  const stabilizedValue = u.capRatePct > 0 ? u.noi / (u.capRatePct / 100) : 0;
  const annualDebtService =
    seniorAmount > 0 ? calculatePMT(seniorRate, u.termYears, seniorAmount) * 12 : 0;

  return {
    seniorAmount,
    annualDebtService,
    stabilizedValue,
    dscr: annualDebtService > 0 ? u.noi / annualDebtService : 0,
    ltv: stabilizedValue > 0 ? seniorAmount / stabilizedValue : 0,
    debtYield: seniorAmount > 0 ? u.noi / seniorAmount : 0,
    equityShare: total > 0 ? equity / total : 0,
  };
}

/** The four tests, formatted for display. */
export function underwritingTests(
  sources: StackSource[],
  total: number,
  u: UnderwritingContext,
): UnderwritingTest[] {
  const s = scoreStack(sources, total, u);
  return [
    { label: 'DSCR (senior)', value: `${s.dscr.toFixed(2)}x`, threshold: '≥ 1.25x', pass: s.dscr >= 1.25 },
    { label: 'LTV (senior)', value: `${(s.ltv * 100).toFixed(1)}%`, threshold: '≤ 70%', pass: s.ltv > 0 && s.ltv <= 0.7 },
    { label: 'Debt yield', value: `${(s.debtYield * 100).toFixed(1)}%`, threshold: '≥ 10%', pass: s.debtYield >= 0.1 },
    { label: 'Sponsor equity', value: `${(s.equityShare * 100).toFixed(1)}%`, threshold: '≥ 25%', pass: s.equityShare >= 0.25 },
  ];
}
